import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import ReactQuill from 'react-quill-new';
import 'quill/dist/quill.snow.css';

const AdminNewsForm = ({ token }) => {
    const { articleId } = useParams(); // For editing existing articles
    const navigate = useNavigate();
    const apiBaseUrl = import.meta.env.VITE_API_URL;

    const [title, setTitle] = useState('');
    const [slug, setSlug] = useState('');
    const [summary, setSummary] = useState('');
    const [bodyContent, setBodyContent] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [publishDate, setPublishDate] = useState(new Date().toISOString().split('T')[0]); // Default to today
    const [author, setAuthor] = useState('TDYK');
    const [isPublished, setIsPublished] = useState(true);
    
    const [selectedFile, setSelectedFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [uploadMessage, setUploadMessage] = useState('');
    const [formMessage, setFormMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

    const isEditing = Boolean(articleId);

    useEffect(() => {
        const fetchArticleData = async (id) => {
            setIsLoading(true);
            setFormMessage('');
            try {
                const response = await fetch(`${apiBaseUrl}/api/news/${id}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                setTitle(data.title || '');
                setSlug(data.slug || '');
                setSummary(data.summary || '');
                setBodyContent(data.body_content || '');
                setImageUrl(data.image_url || '');
                setPublishDate(data.publish_date ? new Date(data.publish_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]);
                setAuthor(data.author || 'TDYK');
                setIsPublished(data.is_published !== undefined ? data.is_published : true);
                
                if (data.image_url) {
                    try {
                        const fullImageUrl = new URL(data.image_url, apiBaseUrl.startsWith('http') ? apiBaseUrl : window.location.origin).href;
                        setImagePreview(fullImageUrl);
                    } catch (e) {
                        console.error("Error constructing image preview URL from fetched data:", e);
                        setImagePreview(''); 
                    }
                }
                setSlugManuallyEdited(true); // Assume slug is set and shouldn't be auto-generated on title change

            } catch (err) {
                console.error("Error fetching article data:", err);
                setFormMessage(`Error fetching article: ${err.message}`);
            } finally {
                setIsLoading(false);
            }
        };

        if (isEditing && articleId && token) {
            fetchArticleData(articleId);
        }
    }, [articleId, isEditing, token, apiBaseUrl]);

        const generateSlug = (titleString) => {
        return titleString
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '-') // Replace spaces with -
            .replace(/[^a-z0-9-]/g, '') // Remove invalid chars
            .replace(/-+/g, '-'); // Replace multiple - with single -
    };

    const handleTitleChange = (e) => {
        const newTitle = e.target.value;
        setTitle(newTitle);
        if (!slugManuallyEdited) {
            setSlug(generateSlug(newTitle));
        }
    };

    const handleSlugChange = (e) => {
        setSlug(e.target.value);
        setSlugManuallyEdited(true);
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
            setImagePreview(URL.createObjectURL(file));
            setUploadMessage('');
        }
    };

    const handleImageUpload = async () => {
        if (!selectedFile) {
            setUploadMessage('Please select an image file first.');
            return;
        }
        setIsLoading(true);
        setUploadMessage('Uploading image...');

        const formData = new FormData();
        formData.append('newsImage', selectedFile);

        try {
            const response = await fetch(`${apiBaseUrl}/api/upload/news-image`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formData,
            });

            const result = await response.json();
            if (!response.ok) {
                throw new Error(result.error || `HTTP error! status: ${response.status}`);
            }

            setImageUrl(result.imageUrl); // Save the returned image URL
            setUploadMessage(`Image uploaded successfully: ${result.imageUrl}`);
            try {
                const fullImageUrl = new URL(result.imageUrl, apiBaseUrl).href;
                setImagePreview(fullImageUrl);
            } catch (e) {
                console.error("Error constructing image preview URL:", e);
                console.error("apiBaseUrl:", apiBaseUrl);
                console.error("result.imageUrl:", result.imageUrl);
                setImagePreview(''); // Or some placeholder for a broken image
                setUploadMessage(`Image uploaded, but preview failed: ${result.imageUrl}`);
            } // Show uploaded image from server

        } catch (err) {
            console.error('Image upload error:', err);
            setUploadMessage(`Image upload failed: ${err.message}`);
            setImageUrl(''); // Clear image URL on failure
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsLoading(true);
        setFormMessage('');

        if (!imageUrl && !isEditing) { // Require image for new articles
            setFormMessage('Please upload an article image before submitting.');
            setIsLoading(false);
            return;
        }

        const articleData = {
            title,
            slug,
            summary,
            body_content: bodyContent, // Ensure this matches backend expectation
            image_url: imageUrl,
            publish_date: publishDate,
            author,
            is_published: isPublished,
        };

        const endpoint = isEditing ? `${apiBaseUrl}/api/news/${articleId}` : `${apiBaseUrl}/api/news`;
        const method = isEditing ? 'PUT' : 'POST';

        try {
            const response = await fetch(endpoint, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(articleData),
            });

            const result = await response.json();
            if (!response.ok) {
                throw new Error(result.error || `Failed to ${isEditing ? 'update' : 'create'} article`);
            }
            setFormMessage(`Article ${isEditing ? 'updated' : 'created'} successfully!`);
            setTimeout(() => navigate('/admin/news'), 2000); // Redirect after a short delay

        } catch (err) {
            console.error(`Error ${isEditing ? 'updating' : 'creating'} article:`, err);
            setFormMessage(`Error: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Basic form structure - will be expanded
    return (
        <>
            <style>
                {`
                    .ql-editor {
                        min-height: 20rem;
                    }
                `}
            </style>
            <div className="container mx-auto p-4">
            <h1 className="text-2xl font-bold mb-6">{isEditing ? 'Edit' : 'Create'} News Article</h1>
            <form onSubmit={handleSubmit} className="space-y-6 p-8 shadow-md rounded-lg">
                <div>
                    <label htmlFor="author" className="block text-sm font-medium text-gray-700 mb-1">Author</label>
                    <input type="text" id="author" value={author} onChange={e => setAuthor(e.target.value)}
                           className="mt-1 block w-1/2 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
                </div>

                <div className="flex flex-col sm:flex-row sm:space-x-4">
                    <div className="w-full sm:w-1/2">
                        <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                        <input type="text" id="title" value={title} onChange={handleTitleChange} required 
                               className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
                    </div>
                    <div className="w-full sm:w-1/2 mt-4 sm:mt-0">
                        <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
                        <input type="text" id="slug" value={slug} onChange={handleSlugChange} required 
                               className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
                        <button type="button" onClick={() => { setSlug(generateSlug(title)); setSlugManuallyEdited(false); }} 
                                className="mt-1 text-xs text-indigo-600 hover:text-indigo-800">
                            Auto-generate from title
                        </button>
                    </div>
                </div>

                <div>
                    <label htmlFor="summary" className="block text-sm font-medium text-gray-700 mb-1">Summary</label>
                    <textarea id="summary" value={summary} onChange={e => setSummary(e.target.value)} rows="3" 
                              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"></textarea>
                </div>

                <div>
                    <label htmlFor="bodyContent" className="block text-sm font-medium text-gray-700 mb-1">Body Content</label>
                    <ReactQuill
                        theme="snow"
                        value={bodyContent}
                        onChange={setBodyContent}
                        className="mt-1 bg-white min-h-[20rem]" // bg-white ensures the editor area has a background
                    />
                </div>

                <div>
                    <label htmlFor="articleImage" className="block text-sm font-medium text-gray-700 mb-1">Article Image</label>
                    <input type="file" id="articleImage" onChange={handleFileChange} accept="image/*" 
                           className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"/>
                    {imagePreview && (
                        <div className="mt-4">
                            <img src={imagePreview} alt="Preview" className="max-h-48 w-1/2 rounded" />
                        </div>
                    )}
                    <button type="button" onClick={handleImageUpload} disabled={isLoading || !selectedFile}
                            className="mt-2 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50">
                        {isLoading && uploadMessage.startsWith('Uploading') ? 'Uploading...' : 'Upload Image'}
                    </button>
                    {uploadMessage && <p className={`mt-2 text-sm ${imageUrl ? 'text-green-600' : 'text-red-600'}`}>{uploadMessage}</p>}
                </div>


                <div>
                    <label htmlFor="publishDate" className="block text-sm font-medium text-gray-700 mb-1">Publish Date</label>
                    <input type="date" id="publishDate" value={publishDate} onChange={e => setPublishDate(e.target.value)} required 
                           className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
                </div>

                <div className="flex items-center">
                    <input type="checkbox" id="isPublished" checked={isPublished} onChange={e => setIsPublished(e.target.checked)} 
                           className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" />
                    <label htmlFor="isPublished" className="ml-2 block text-sm text-gray-900">Published</label>
                </div>

                <button type="submit" disabled={isLoading}
                        className="w-full py-2 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md shadow-sm disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
                    {isLoading ? 'Submitting...' : (isEditing ? 'Update Article' : 'Create Article')}
                </button>
                {formMessage && <p className={`mt-4 text-center text-sm ${formMessage.startsWith('Error') ? 'text-red-600' : 'text-green-600'}`}>{formMessage}</p>}
            </form>
        </div>
        </>
    );
};

export default AdminNewsForm;
